import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ImageIcon } from 'lucide-react';

import CommonLoader from '../../../../../components/common-loader/CommonLoader';
import ConfirmationModal from '../../../../../components/confirmation-modal/ConfirmationModal';
import EmptyState from '../../../../../components/empty-state/EmptyState';
import { MEDIA_STUDIO_ASSET_PREVIEW_TYPES } from '../../../../../constants';
import DeleteLabel from '../../../../../projects/projects-list/components/DeleteLabel';
import {
  deleteMediaStudioImage,
  getMediaStudioImages,
  uploadMediaStudioImage,
} from '../../../../../redux/settings/media-studio/mediaStudioSettingsActions';
import AssetCard from '../asset-card/AssetCard';
import AssetDropArea from '../asset-drop-area/AssetDropArea';
import AssetGrid from '../asset-grid/AssetGrid';
import AssetPreviewModal from '../asset-preview-modal/AssetPreviewModal';

const IMAGE_ACCEPT = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/vnd.adobe.photoshop': ['.psd', '.psb'],
  'image/tiff': ['.tif', '.tiff'],
};

function ImageAssets() {
  const { t } = useTranslation('settings');
  const dispatch = useDispatch();
  const { images, isImagesLoading, uploadingImages, deletingImageIds } = useSelector(
    (state) => state.mediaStudioSettings,
  );
  const [preview, setPreview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(getMediaStudioImages());
  }, [dispatch]);

  const handleFiles = (files) => {
    setError(null);
    files.forEach((file) => dispatch(uploadMediaStudioImage(file)));
  };

  const handleRejected = () => setError('mediaStudioImageUnsupported');

  const renderContent = () => {
    if (isImagesLoading) {
      return <CommonLoader isLoading />;
    }

    if (!images.length && !uploadingImages.length) {
      return (
        <EmptyState
          icon={<ImageIcon size={28} strokeWidth={1.5} />}
          title={t('mediaStudioNoImages')}
          description={t('mediaStudioNoImagesHint')}
        />
      );
    }

    return (
      <AssetGrid>
        {images.map((image) => (
          <AssetCard
            key={image.id}
            title={image.label}
            isLoading={deletingImageIds.includes(image.id)}
            onPreview={() => setPreview({ type: MEDIA_STUDIO_ASSET_PREVIEW_TYPES.IMAGE, item: image })}
            onDelete={() => setDeleteTarget(image)}>
            <img src={image.url} alt='' />
          </AssetCard>
        ))}
        {uploadingImages.map((image) => (
          <AssetCard key={image.id} title={image.label} isLoading />
        ))}
      </AssetGrid>
    );
  };

  return (
    <AssetDropArea
      accept={IMAGE_ACCEPT}
      error={error}
      onErrorClose={() => setError(null)}
      onFiles={handleFiles}
      onRejected={handleRejected}>
      {renderContent()}

      <AssetPreviewModal asset={preview} onClose={() => setPreview(null)} />

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => dispatch(deleteMediaStudioImage(deleteTarget.id))}
        subLabel={<DeleteLabel name={deleteTarget?.label} />}
      />
    </AssetDropArea>
  );
}

export default ImageAssets;
